import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getCoadminActivity } from "@/lib/api";
import { formatDateTime, unitOf } from "@/lib/fabricData";

const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

export function CoAdminActivity({ token }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nameQuery, setNameQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    try { setOrders(await getCoadminActivity(token)); } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const nq = nameQuery.trim().toLowerCase();
    return orders.filter((o) => {
      if (nq && !(o.client_name || "").toLowerCase().includes(nq)) return false;
      if (o.created_at) {
        const day = String(o.created_at).slice(0, 10); // yyyy-mm-dd
        if (dateFrom && day < dateFrom) return false;
        if (dateTo && day > dateTo) return false;
      } else if (dateFrom || dateTo) {
        return false;
      }
      return true;
    });
  }, [orders, nameQuery, dateFrom, dateTo]);

  const hasFilters = nameQuery.trim() || dateFrom || dateTo;
  const clearFilters = () => { setNameQuery(""); setDateFrom(""); setDateTo(""); };

  return (
    <div className="mt-10 space-y-4" data-testid="coadmin-activity">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-black text-2xl tracking-tight text-white">Co-Admin Activity Log</h2>
          <p className="text-xs text-white/50">Every order placed by the co-admin, on behalf of a customer.</p>
        </div>
        <Button onClick={load} data-testid="coadmin-activity-refresh" variant="outline" className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 border border-white/10 bg-[#0A0A0A] p-3" data-testid="coadmin-activity-filters">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">Customer name</Label>
          <Input value={nameQuery} onChange={(e) => setNameQuery(e.target.value)} data-testid="coadmin-activity-filter-name" placeholder="Search by customer" className="rounded-none h-9 bg-[#141414] border-white/10 text-white text-sm" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="coadmin-activity-filter-from" className="rounded-none h-9 bg-[#141414] border-white/10 text-white text-sm" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.2em] text-white/40 block mb-1">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="coadmin-activity-filter-to" className="rounded-none h-9 bg-[#141414] border-white/10 text-white text-sm" />
        </div>
        {hasFilters && (
          <div className="flex items-end">
            <Button onClick={clearFilters} data-testid="coadmin-activity-filter-clear" variant="outline" className="rounded-none h-9 border-white/20 bg-transparent text-white hover:bg-white hover:text-black text-xs uppercase tracking-wider">
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          </div>
        )}
      </div>

      <p className="text-xs text-white/40" data-testid="coadmin-activity-count">Showing {filtered.length} of {orders.length} co-admin order{orders.length === 1 ? "" : "s"}</p>

      {filtered.length === 0 ? (
        <p className="text-sm text-white/40 border border-white/10 px-4 py-8 text-center">{orders.length === 0 ? "No co-admin orders yet." : "No orders match the current filters."}</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => (
            <div key={o.id} className="border border-white/10 bg-[#0A0A0A]" data-testid={`coadmin-activity-card-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-white/10 bg-[#141414]">
                <div>
                  <p className="text-sm text-white font-medium">CO-AD — {o.client_name}</p>
                  <p className="text-xs text-white/50">{o.phone ? o.phone + " · " : ""}{o.created_at ? formatDateTime(o.created_at) : "—"}</p>
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-white/20 text-white/70">{o.status || "Pending"}</span>
              </div>
              <div className="divide-y divide-white/5">
                {(o.items || []).map((it, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2 text-sm text-white/90" data-testid={`coadmin-activity-item-${o.id}-${i}`}>
                    <span className="flex-1 truncate">{it.fabric} <span className="text-white/40">·</span> {it.color}</span>
                    <span className="tabular">{fmt(it.quantity_kg)} {unitOf(it.fabric)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
